"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

type FieldErrors = Partial<Record<string, string>>;

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validate(fields: Record<string, string>): FieldErrors {
  const errors: FieldErrors = {};
  if (!fields.name.trim()) errors.name = "Name is required";
  if (!fields.title.trim()) errors.title = "Title is required";
  if (!fields.company.trim()) errors.company = "Company is required";
  if (!fields.email.trim()) {
    errors.email = "Email is required";
  } else if (!validateEmail(fields.email)) {
    errors.email = "Please enter a valid email address";
  }
  return errors;
}

const inputBase =
  "w-full bg-[#0C0C0E] border text-[#F2EFE9] text-sm px-4 py-3 rounded-sm placeholder:text-[#3A3A40] focus:outline-none transition-colors";
const inputNormal =
  "border-[rgba(242,239,233,0.1)] focus:border-[rgba(184,152,106,0.4)]";
const inputError =
  "border-[#E5484D] focus:border-[#E5484D]";

export default function ContactSection() {
  const [formState, setFormState] = useState({
    name: "",
    title: "",
    company: "",
    email: "",
    service: "",
    message: "",
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field on change if it was touched
    if (touched[name]) {
      const newErrors = validate({ ...formState, [name]: value });
      setErrors((prev) => {
        const updated = { ...prev };
        if (newErrors[name]) {
          updated[name] = newErrors[name];
        } else {
          delete updated[name];
        }
        return updated;
      });
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Validate this field on blur
    const newErrors = validate(formState);
    setErrors((prev) => {
      const updated = { ...prev };
      if (newErrors[name]) {
        updated[name] = newErrors[name];
      } else {
        delete updated[name];
      }
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(false);

    // Mark all required fields as touched
    setTouched({ name: true, title: true, company: true, email: true });

    const newErrors = validate(formState);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setSubmitting(true);

    try {
      const formBody = new URLSearchParams({
        "form-name": "contact",
        ...formState,
      });

      const response = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody.toString(),
      });

      if (!response.ok) throw new Error("Form submission failed");

      setSubmitted(true);
    } catch {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (name: string) =>
    touched[name] && errors[name] ? (
      <p id={`${name}-error`} role="alert" className="text-[#E5484D] text-[10px] font-medium mt-1.5">{errors[name]}</p>
    ) : null;

  const fieldClass = (name: string) =>
    `${inputBase} ${touched[name] && errors[name] ? inputError : inputNormal}`;

  return (
    <section id="contact" aria-label="Contact" className="py-32 bg-[#0C0C0E] relative overflow-hidden">
      {/* Background glow */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] opacity-[0.04] pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at center, #B8986A, transparent 70%)",
        }}
      />

      <div className="max-w-8xl mx-auto px-6 lg:px-12 relative">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Left — CTA Copy */}
          <div>
            <div className="flex items-center gap-4 mb-6 reveal-hidden" data-reveal>
              <div className="w-8 h-[1px] bg-[#B8986A]" />
              <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#B8986A]">
                Start a Conversation
              </span>
            </div>
            <h2
              className="font-fraunces text-[clamp(2.5rem,5vw,4.5rem)] font-light leading-[1.05] tracking-tight text-[#F2EFE9] mb-8 reveal-hidden"
              data-reveal
            >
              Tell Us What&apos;s
              <br />
              <span className="italic text-gradient-gold">Broken.</span>
            </h2>
            <p className="text-[#A09D97] text-lg font-light leading-relaxed mb-12 reveal-hidden delay-200" data-reveal>
              Drop us a note and we&apos;ll get back to you. No sales pitch, no pressure. Let&apos;s have an honest conversation about what you&apos;re dealing with and whether JAMO can help.
            </p>

            {/* Contact Details */}
            <div className="space-y-6 reveal-hidden delay-300" data-reveal>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 border border-[rgba(184,152,106,0.2)] rounded-sm flex items-center justify-center flex-shrink-0">
                  <Icon name="EnvelopeIcon" size={16} className="text-[#B8986A]" />
                </div>
                <div>
                  <div className="text-[9px] font-bold tracking-[0.2em] uppercase text-[#6B6864] mb-0.5">
                    Email
                  </div>
                  <a
                    href="mailto:hello@jamollc.com"
                    className="text-[#A09D97] text-sm hover:text-[#B8986A] transition-colors"
                  >
                    hello@jamollc.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 border border-[rgba(184,152,106,0.2)] rounded-sm flex items-center justify-center flex-shrink-0">
                  <Icon name="MapPinIcon" size={16} className="text-[#B8986A]" />
                </div>
                <div>
                  <div className="text-[9px] font-bold tracking-[0.2em] uppercase text-[#6B6864] mb-0.5">
                    Based In
                  </div>
                  <p className="text-[#A09D97] text-sm">
                    California
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="reveal-hidden delay-200" data-reveal>
            {!submitted ? (
              <form
                onSubmit={handleSubmit}
                className="glass-card p-10 space-y-6"
                noValidate
                aria-label="Contact inquiry form"
                name="contact"
              >
                <input type="hidden" name="form-name" value="contact" />

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-name" className="block text-[9px] font-bold tracking-[0.2em] uppercase text-[#6B6864] mb-2">
                      Full Name *
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Jane Smith"
                      required
                      aria-required="true"
                      aria-invalid={touched.name && !!errors.name}
                      aria-describedby={touched.name && errors.name ? "name-error" : undefined}
                      className={fieldClass("name")}
                    />
                    {fieldError("name")}
                  </div>
                  <div>
                    <label htmlFor="contact-title" className="block text-[9px] font-bold tracking-[0.2em] uppercase text-[#6B6864] mb-2">
                      Title *
                    </label>
                    <input
                      id="contact-title"
                      type="text"
                      name="title"
                      value={formState.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="VP of Engineering"
                      required
                      aria-required="true"
                      aria-invalid={touched.title && !!errors.title}
                      aria-describedby={touched.title && errors.title ? "title-error" : undefined}
                      className={fieldClass("title")}
                    />
                    {fieldError("title")}
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-company" className="block text-[9px] font-bold tracking-[0.2em] uppercase text-[#6B6864] mb-2">
                    Company *
                  </label>
                  <input
                    id="contact-company"
                    type="text"
                    name="company"
                    value={formState.company}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Your Company Name"
                    required
                    aria-required="true"
                    aria-invalid={touched.company && !!errors.company}
                    aria-describedby={touched.company && errors.company ? "company-error" : undefined}
                    className={fieldClass("company")}
                  />
                  {fieldError("company")}
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-[9px] font-bold tracking-[0.2em] uppercase text-[#6B6864] mb-2">
                    Work Email *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formState.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="jane@yourcompany.com"
                    required
                    aria-required="true"
                    aria-invalid={touched.email && !!errors.email}
                    aria-describedby={touched.email && errors.email ? "email-error" : undefined}
                    className={fieldClass("email")}
                  />
                  {fieldError("email")}
                </div>

                <div>
                  <label htmlFor="contact-service" className="block text-[9px] font-bold tracking-[0.2em] uppercase text-[#6B6864] mb-2">
                    Area of Interest
                  </label>
                  <select
                    id="contact-service"
                    name="service"
                    value={formState.service}
                    onChange={handleChange}
                    className="w-full bg-[#0C0C0E] border border-[rgba(242,239,233,0.1)] text-[#A09D97] text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-[rgba(184,152,106,0.4)] transition-colors"
                  >
                    <option value="">Select a service area</option>
                    <option value="fractional-leadership">Fractional CTO / CIO / CISO</option>
                    <option value="cloud-architecture">Cloud Architecture & Migration</option>
                    <option value="devops">DevOps & Automation</option>
                    <option value="security">Security & Zero Trust</option>
                    <option value="team-building">Team Building & Operations</option>
                    <option value="monitoring">Monitoring & Reliability</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-[9px] font-bold tracking-[0.2em] uppercase text-[#6B6864] mb-2">
                    What Are You Dealing With?
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formState.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Briefly describe your current challenge or objective..."
                    className="w-full bg-[#0C0C0E] border border-[rgba(242,239,233,0.1)] text-[#F2EFE9] text-sm px-4 py-3 rounded-sm placeholder:text-[#3A3A40] focus:outline-none focus:border-[rgba(184,152,106,0.4)] transition-colors resize-none"
                  />
                </div>

                {submitError && (
                  <div role="alert" className="bg-[rgba(229,72,77,0.1)] border border-[rgba(229,72,77,0.3)] rounded-sm px-4 py-3">
                    <p className="text-[#E5484D] text-sm font-medium">
                      Something went wrong. Please try again or email us directly at hello@jamollc.com.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  aria-busy={submitting}
                  className="btn-primary w-full text-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{submitting ? "Sending..." : "Send It Over"}</span>
                </button>

                <p className="text-[#3A3A40] text-[10px] text-center font-medium">
                  All inquiries are treated with strict confidentiality.
                </p>
              </form>
            ) : (
              <div role="status" aria-label="Inquiry submitted successfully" className="glass-card p-16 flex flex-col items-center justify-center text-center min-h-[400px]">
                <div className="w-16 h-16 border border-[rgba(184,152,106,0.3)] rounded-sm flex items-center justify-center mb-8">
                  <Icon name="CheckIcon" size={28} className="text-[#B8986A]" />
                </div>
                <h3 className="font-fraunces text-3xl font-light text-[#F2EFE9] mb-4">
                  Inquiry Received
                </h3>
                <p className="text-[#A09D97] text-sm font-light leading-relaxed max-w-sm mb-6">
                  Thanks for reaching out. We&apos;ve sent a confirmation to <span className="text-[#F2EFE9] font-medium">{formState.email}</span>. A member of our team will follow up shortly.
                </p>
                <div className="border-t border-[rgba(242,239,233,0.06)] pt-6 w-full max-w-xs">
                  <p className="text-[#6B6864] text-xs font-medium">
                    Don&apos;t see it? Check your spam folder or reach us directly at{" "}
                    <a href="mailto:hello@jamollc.com" className="text-[#B8986A] hover:underline">
                      hello@jamollc.com
                    </a>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
